import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// =====================================================
// Shifts
// =====================================================

export interface ShiftDefinition {
  id: string;
  location_id: string;
  code: string;
  name: string;
  start_time: string; // HH:MM
  end_time: string;
  duration_hours: number;
  is_active: boolean;
  /** 'manual' — создана/подтверждена человеком; 'auto' — сгенерирована автоматически */
  source: string;
  position_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface ShiftCreateInput {
  code: string;
  name: string;
  start_time: string;
  end_time: string;
  duration_hours: number;
  position_ids?: string[];
}

export interface ShiftUpdateInput extends Partial<ShiftCreateInput> {
  is_active?: boolean;
}

const SHIFT_KEYS = {
  all: ['optimization-shifts'] as const,
  list: (locationId: string) => [...SHIFT_KEYS.all, 'list', locationId] as const,
  detail: (id: string) => [...SHIFT_KEYS.all, 'detail', id] as const,
};

export const useShifts = (locationId: string | null) =>
  useQuery<ShiftDefinition[]>({
    queryKey: SHIFT_KEYS.list(locationId ?? ''),
    queryFn: () =>
      api.get(`/api/v1/optimization/locations/${locationId}/shifts`),
    enabled: !!locationId,
  });

export const useCreateShift = (locationId: string | null) => {
  const qc = useQueryClient();
  return useMutation<ShiftDefinition, Error, ShiftCreateInput>({
    mutationFn: (data) =>
      api.post(`/api/v1/optimization/locations/${locationId}/shifts`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIFT_KEYS.all });
    },
  });
};

export const useUpdateShift = () => {
  const qc = useQueryClient();
  return useMutation<
    ShiftDefinition,
    Error,
    { id: string; data: ShiftUpdateInput }
  >({
    mutationFn: ({ id, data }) =>
      api.patch(`/api/v1/optimization/shifts/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIFT_KEYS.all });
    },
  });
};

export const useDeleteShift = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete(`/api/v1/optimization/shifts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIFT_KEYS.all });
    },
  });
};

export interface GenerateShiftsResult {
  employees_analyzed: number;
  created: ShiftDefinition[];
  skipped_existing: Array<{ start_time: string; end_time: string }>;
  skipped_too_long: Array<{
    start_time: string;
    end_time: string;
    duration_hours: number;
    employees: number;
  }>;
  skipped_few_employees: number;
  max_shift_hours: number;
}

/** Автосоздание смен из назначенных графиков сотрудников локации. */
export const useGenerateShiftsFromSchedules = (locationId: string | null) => {
  const qc = useQueryClient();
  return useMutation<GenerateShiftsResult, Error, void>({
    mutationFn: () =>
      api.post(
        `/api/v1/optimization/locations/${locationId}/shifts/generate-from-schedules`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SHIFT_KEYS.all });
    },
  });
};

// =====================================================
// Labor Rules (per position)
// =====================================================

export interface LaborRule {
  id: string;
  position_id: string;
  position_name: string | null;
  max_hours_per_week: number;
  min_rest_hours_between_shifts: number;
  min_days_off_per_week: number;
  max_consecutive_work_days: number;
  max_hours_per_day: number;
  allow_night_shifts: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LaborRuleUpsertInput {
  max_hours_per_week?: number;
  min_rest_hours_between_shifts?: number;
  min_days_off_per_week?: number;
  max_consecutive_work_days?: number;
  max_hours_per_day?: number;
  allow_night_shifts?: boolean;
  is_active?: boolean;
}

const RULE_KEYS = {
  all: ['optimization-labor-rules'] as const,
  list: (positionId?: string) => [...RULE_KEYS.all, 'list', positionId] as const,
};

export const useLaborRules = (positionId?: string) =>
  useQuery<LaborRule[]>({
    queryKey: RULE_KEYS.list(positionId),
    queryFn: () =>
      api.get('/api/v1/optimization/labor-rules', {
        params: positionId ? { position_id: positionId } : undefined,
      }),
  });

export const useUpsertLaborRule = () => {
  const qc = useQueryClient();
  return useMutation<
    LaborRule,
    Error,
    { positionId: string; data: LaborRuleUpsertInput }
  >({
    mutationFn: ({ positionId, data }) =>
      api.put(
        `/api/v1/optimization/labor-rules/by-position/${positionId}`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RULE_KEYS.all });
    },
  });
};

export const useDeleteLaborRule = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete(`/api/v1/optimization/labor-rules/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RULE_KEYS.all });
    },
  });
};

// =====================================================
// Min Staffing (per location) — минимальный штат (floor для солвера)
// =====================================================

export interface MinStaffingRule {
  id: string;
  location_id: string;
  position_id: string;
  position_name: string;
  /** 0=Пн..6=Вс, null = все дни недели */
  day_of_week: number | null;
  hour_from: number;
  hour_to: number;
  min_employees: number;
}

export interface MinStaffingRuleInput {
  position_id: string;
  day_of_week?: number | null;
  hour_from: number;
  hour_to: number;
  min_employees: number;
}

const MIN_STAFFING_KEYS = {
  all: ['optimization-min-staffing'] as const,
  list: (locationId: string) =>
    [...MIN_STAFFING_KEYS.all, 'list', locationId] as const,
};

export const useMinStaffing = (locationId: string | null) =>
  useQuery<MinStaffingRule[]>({
    queryKey: MIN_STAFFING_KEYS.list(locationId ?? ''),
    queryFn: () =>
      api.get(`/api/v1/optimization/locations/${locationId}/min-staffing`),
    enabled: !!locationId,
  });

/** Полная замена набора правил локации (идемпотентный PUT). */
export const useReplaceMinStaffing = (locationId: string | null) => {
  const qc = useQueryClient();
  return useMutation<MinStaffingRule[], Error, MinStaffingRuleInput[]>({
    mutationFn: (rules) =>
      api.put(`/api/v1/optimization/locations/${locationId}/min-staffing`, {
        rules,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MIN_STAFFING_KEYS.all });
    },
  });
};

// =====================================================
// Optimization Config (per location)
// =====================================================

export interface OptimizationConfig {
  id: string;
  location_id: string;
  weight_fot: number;
  weight_understaffing: number;
  weight_overtime: number;
  solver_timeout_seconds: number;
  forecast_source: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConfigUpsertInput {
  weight_fot?: number;
  weight_understaffing?: number;
  weight_overtime?: number;
  solver_timeout_seconds?: number;
  forecast_source?: string;
  is_active?: boolean;
}

const CFG_KEYS = {
  all: ['optimization-config'] as const,
  detail: (locationId: string) => [...CFG_KEYS.all, locationId] as const,
};

export const useOptimizationConfig = (locationId: string | null) =>
  useQuery<OptimizationConfig>({
    queryKey: CFG_KEYS.detail(locationId ?? ''),
    queryFn: () =>
      api.get(`/api/v1/optimization/locations/${locationId}/config`),
    enabled: !!locationId,
  });

export const useUpsertOptimizationConfig = (locationId: string | null) => {
  const qc = useQueryClient();
  return useMutation<OptimizationConfig, Error, ConfigUpsertInput>({
    mutationFn: (data) =>
      api.put(`/api/v1/optimization/locations/${locationId}/config`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CFG_KEYS.all });
    },
  });
};

// =====================================================
// Hourly Profile refresh
// =====================================================

export interface RefreshHourlyProfileResponse {
  locationId: string;
  departmentId: string;
  departmentName: string | null;
  fromDate: string;
  toDate: string;
  hourlyRecords: number;
  daysSampled: number;
  rowsUpserted: number;
  fallbackToUniform: boolean;
  computedAt: string;
}

export const useRefreshHourlyProfile = () => {
  const qc = useQueryClient();
  return useMutation<RefreshHourlyProfileResponse, Error, string>({
    mutationFn: (locationId) =>
      api.post(
        `/api/v1/optimization/locations/${locationId}/refresh-hourly-profile`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['optimization-config'] });
    },
  });
};
